import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  LoadingState,
  type TranscriptFragment,
} from "@repo/app-types/client/client";
import type { Account } from "@repo/app-types/entities/account";
import type { Activity } from "@repo/app-types/entities/activity";
import type { Contact } from "@repo/app-types/entities/contact";
import type {
  InterviewDecision,
  InterviewLoaded,
  InterviewMetadata,
  InterviewUpdate,
} from "@repo/app-types/entities/interview";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import merge from "lodash-es/merge.js";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import { interviewApi } from "../../api/entities/interview.js";
import type { InterviewWithContactAndActivities } from "../../app/admin_client_types.js";
import type { RootState } from "../../app/store.js";
import { selectAccountsDictionary } from "../accounts/accountsState.js";
import { selectAllActivities } from "../activities/activitiesState.js";
import { selectContactsDictionary } from "../contacts/contactsState.js";
import { fetchInsightsAsync } from "../insights/insightsSlice.js";
import { fetchProjectById } from "../projects/projectsSlice.js";
import { transcriptFragmentsAdapter } from "../transcriptFragments/transcriptFragmentsState.js";

/*
 * State
 */

export type InterviewStatusFilter = "active" | "pending";

export interface InterviewsParameters {
  projectId: string;
  page: number;
  search_text?: string;
  min_assessment_score?: number;
  status_filter?: InterviewStatusFilter;
  decision_filter?: InterviewDecision | "pending";
  // In the future add things like date ranges or search filters
}

export const interviewsAdapter = createEntityAdapter<InterviewLoaded>();

const initialState = interviewsAdapter.getInitialState<{
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  parameters: InterviewsParameters | undefined;
  totalPages: number | undefined;
  interviewIds: string[]; // The interiews corresponding to the current parameters
  interviewIdToMatchingFragmentId: Record<string, string>;
  focusedInterviewId: string | undefined;
  focusedInterviewLoadingState: LoadingState;
}>({
  status: "idle",
  error: null,
  parameters: undefined,
  totalPages: undefined,
  interviewIds: [],
  interviewIdToMatchingFragmentId: {},
  focusedInterviewId: undefined,
  focusedInterviewLoadingState: LoadingState.LOADED,
});

/*
 * Thunks
 */

interface FetchInterviewsPayload {
  interviews: InterviewLoaded[];
  contacts: Contact[];
  accounts: Account[];
  activities: Activity[];
  matching_transcript_fragments?: TranscriptFragment[];
  total_pages: number;
}

// Fetch all interviews, associated contacts, activities, and the interview link for a particular project
export const fetchNonEmptyInterviewsForProject = createAsyncThunk<
  FetchInterviewsPayload,
  InterviewsParameters,
  { rejectValue: string }
>(
  "interviews/fetchDependenciesForProject",
  async (parameters: InterviewsParameters, thunkAPI) => {
    const {
      projectId,
      page,
      search_text,
      min_assessment_score,
      status_filter,
      decision_filter,
    } = parameters;

    try {
      const axios = await getAxiosInstanceWithAuth();

      const params: any = {
        page,
      };
      if (search_text) params.search_text = search_text;

      if (min_assessment_score !== undefined) {
        params.min_assessment_score = min_assessment_score;
      }

      if (status_filter) {
        params.status_filter = status_filter;
      }

      if (decision_filter) {
        params.decision_filter = decision_filter;
      }

      const response = await axios.get(`/projects/${projectId}/interviews`, {
        params,
      });

      return thunkAPI.fulfillWithValue(response.data);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const deleteInterviews = createAsyncThunk(
  "interviews/deleteInterviews",
  async (
    args: { interviewIds: string[]; projectId: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const interviews = await dispatch(
        interviewApi.endpoints.bulkUpdateInterviews.initiate({
          projectId: args.projectId,
          interviewIds: args.interviewIds,
          updates: { deleted_at: new Date().toISOString() },
        }),
      ).unwrap();

      // Refetch project to get latest interview counts
      dispatch(fetchProjectById(args.projectId));

      return interviews.map((interview) => interview.id);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const deleteInterview = createAsyncThunk(
  "interviews/deleteInterview",
  async (
    args: { projectId: string; interviewId: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      await axios.delete(
        `/projects/${args.projectId}/interviews/${args.interviewId}`,
      );

      // Refetch project to get latest interview counts
      dispatch(fetchProjectById(args.projectId));

      return args.interviewId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const bulkUpdateInterviews = createAsyncThunk(
  "interviews/bulkUpdateInterviews",
  async (
    args: {
      projectId: string;
      interviewIds: string[];
      update: InterviewUpdate;
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const interviews = await dispatch(
        interviewApi.endpoints.bulkUpdateInterviews.initiate({
          projectId: args.projectId,
          interviewIds: args.interviewIds,
          updates: args.update,
        }),
      ).unwrap();

      // Refetch project to get latest interview counts
      dispatch(fetchProjectById(args.projectId));

      return interviews;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchFocusedInterview = createAsyncThunk<
  { contact: Contact; interview: InterviewLoaded; account: Account | null },
  { projectId: string; interviewId: string },
  { rejectValue: string }
>("interviews/fetchFocusedInterview", async (params, thunkAPI) => {
  const { projectId, interviewId } = params;

  try {
    const axios = await getAxiosInstanceWithAuth();
    const response = await axios.get(
      `/projects/${projectId}/interviews/${interviewId}`,
    );
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message);
  }
});

interface UpdateInterviewPayload {
  projectId: string;
  interviewId: string;
  update: {
    decision?: InterviewDecision | null;
    metadata?: Pick<InterviewMetadata, "assessment_feedback">;
  };
}

export const updateInterview = createAsyncThunk(
  "interviews/updateInterview",
  async (args: UpdateInterviewPayload, { rejectWithValue }) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.patch(
        `/projects/${args.projectId}/interviews/${args.interviewId}`,
        args.update,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

/*
 * Selectors
 */

export const selectInterviewsForProject = (
  state: RootState,
  projectId: string,
) =>
  interviewsAdapter
    .getSelectors()
    .selectAll(state.interviews)
    .filter((interview) => interview.project_id === projectId);

export const buildInterviewsWithContactsAndActivities = (
  interviews: InterviewLoaded[],
  contactsDictionary: Record<string, Contact>,
  accountsDictionary: Record<string, Account>,
  allActivities: Activity[],
): InterviewWithContactAndActivities[] => {
  return interviews.map((interview) => {
    const associatedContact = contactsDictionary[interview.contact_id];
    if (associatedContact === undefined) {
      throw new Error(
        `Missing contact ${interview.contact_id} for interview ${interview.id}`,
      );
    }
    const associatedAccount = associatedContact.account_id
      ? accountsDictionary[associatedContact.account_id]
      : undefined;
    const associatedActivities = allActivities.filter(
      (activity) => activity.interview_id === interview.id,
    );

    const { contact_id, ...restOfInterview } = interview;
    return {
      ...restOfInterview,
      contact: {
        ...associatedContact,
        account: associatedAccount ?? null,
      },
      activities: associatedActivities,
    };
  });
};

// Selects all interviews in the adapter (used by Insights tab)
export const selectNonEmptyInterviewsWithContactsAndActivitiesForProject: (
  state: RootState,
  projectId: string,
) => InterviewWithContactAndActivities[] = createSelector(
  [
    (state: RootState, projectId: string) =>
      selectInterviewsForProject(state, projectId),
    (state: RootState) => selectContactsDictionary(state),
    (state: RootState) => selectAccountsDictionary(state),
    (state: RootState) => selectAllActivities(state.activities),
  ],
  buildInterviewsWithContactsAndActivities,
);

// Selects interviews corresponding to the current parameters (used to show paginated Interviews tab)
export const selectInterviewsForCurrentParameters = createSelector(
  [
    (state: RootState) => state.interviews.interviewIds,
    interviewsAdapter.getSelectors((state: RootState) => state.interviews)
      .selectEntities,
  ],
  (interviewIds, interviews): InterviewLoaded[] =>
    interviewIds.map((id) => interviews[id]).filter(isNotNullNotUndefined),
);

export const selectInterviewsForCurrentParametersWithContactsAndActivities: (
  state: RootState,
) => InterviewWithContactAndActivities[] = createSelector(
  [
    (state: RootState) => selectInterviewsForCurrentParameters(state),
    // For contacts, get the `contacts` sub-state from the RootState
    (state: RootState) => selectContactsDictionary(state),
    (state: RootState) => selectAccountsDictionary(state),
    (state: RootState) => selectAllActivities(state.activities),
  ],
  buildInterviewsWithContactsAndActivities,
);

export const selectFocusedInterviewAndContact: (
  state: RootState,
) => { contact: Contact; interview: InterviewLoaded } | null = createSelector(
  [
    (state: RootState) => state.interviews.focusedInterviewId,
    interviewsAdapter.getSelectors((state: RootState) => state.interviews)
      .selectEntities,
    selectContactsDictionary,
  ],
  (focusedInterviewId, interviewsDictionary, contactsDictionary) => {
    if (!focusedInterviewId) return null;

    const interview = interviewsDictionary[focusedInterviewId];
    if (!interview) return null;

    const contact = contactsDictionary[interview.contact_id];
    if (!contact) return null;

    return { contact, interview };
  },
);

export const selectMatchingTranscriptFragmentForInterview: (
  state: RootState,
  interviewId: string,
) => TranscriptFragment | undefined = createSelector(
  [
    (state: RootState, interviewId) =>
      state.interviews.interviewIdToMatchingFragmentId[interviewId],
    transcriptFragmentsAdapter.getSelectors(
      (state: RootState) => state.transcriptFragments,
    ).selectEntities,
  ],
  (matchingFragmentId, transcriptFragmentsDictionary) => {
    if (!matchingFragmentId) return undefined;

    return transcriptFragmentsDictionary[matchingFragmentId];
  },
);

/*
 * Slice
 */

const interviewsSlice = createSlice({
  name: "interviews",
  initialState,
  reducers: {
    interviewAdded: interviewsAdapter.addOne,
    interviewsAdded: interviewsAdapter.addMany,
    interviewRemoved: interviewsAdapter.removeOne,
    interviewUpdated: interviewsAdapter.updateOne,
    clearFocusedInterview: (state) => {
      state.focusedInterviewId = undefined;
      state.focusedInterviewLoadingState = LoadingState.LOADED;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNonEmptyInterviewsForProject.pending, (state, action) => {
        state.status = "loading";
        state.parameters = action.meta.arg;
      })
      .addCase(fetchNonEmptyInterviewsForProject.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.totalPages = action.payload.total_pages;
        state.interviewIds = action.payload.interviews.map((i) => i.id);

        // Search queries also include list of matching fragments: create a mapping for easy lookups
        if (action.payload.matching_transcript_fragments) {
          const interviewIdToMatchingFragmentId =
            action.payload.matching_transcript_fragments.reduce<
              Record<string, string>
            >((acc, fragment) => {
              acc[fragment.interview_id] = fragment.id;
              return acc;
            }, {});
          state.interviewIdToMatchingFragmentId =
            interviewIdToMatchingFragmentId;
        } else {
          state.interviewIdToMatchingFragmentId = {};
        }

        interviewsAdapter.upsertMany(state, action.payload.interviews);
      })
      .addCase(fetchNonEmptyInterviewsForProject.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(fetchInsightsAsync.fulfilled, (state, action) => {
        interviewsAdapter.addMany(state, action.payload.interviews);
      })
      .addCase(deleteInterviews.fulfilled, (state, action) => {
        interviewsAdapter.removeMany(state, action.payload);
      })
      .addCase(deleteInterview.fulfilled, (state, action) => {
        interviewsAdapter.removeOne(state, action.payload);
        state.interviewIds = state.interviewIds.filter(
          (id) => id !== action.payload,
        );
        delete state.interviewIdToMatchingFragmentId[action.payload];
      })
      .addCase(bulkUpdateInterviews.fulfilled, (state, action) => {
        const updates = action.payload.map((interview) => ({
          id: interview.id,
          changes: interview,
        }));
        interviewsAdapter.updateMany(state, updates);
      })
      .addCase(fetchFocusedInterview.pending, (state, action) => {
        state.focusedInterviewLoadingState = LoadingState.LOADING;
        state.focusedInterviewId = action.meta.arg.interviewId;
      })
      .addCase(fetchFocusedInterview.fulfilled, (state, action) => {
        state.focusedInterviewLoadingState = LoadingState.LOADED;
        interviewsAdapter.upsertOne(state, action.payload.interview);
      })
      .addCase(fetchFocusedInterview.rejected, (state, action) => {
        state.focusedInterviewLoadingState = LoadingState.ERROR;
      })
      .addCase(updateInterview.pending, (state, action) => {
        // Optimistic update
        const draft = state.entities[action.meta.arg.interviewId];
        if (draft) {
          // Deep merge, unlike interviewsAdapter.updateOne
          merge(draft, action.meta.arg.update);
        }
      })
      .addCase(updateInterview.fulfilled, (state, action) => {
        interviewsAdapter.upsertOne(state, action.payload);
      });
  },
});

export const {
  interviewAdded,
  interviewsAdded,
  interviewRemoved,
  interviewUpdated,
  clearFocusedInterview,
} = interviewsSlice.actions;
export default interviewsSlice.reducer;

export const selectInterviewsDictionary = (state: RootState) =>
  state.interviews.entities;

export const selectTotalInterviewPages = (state: RootState) =>
  state.interviews.totalPages;

export const selectInterviewsParameters = (state: RootState) =>
  state.interviews.parameters;

export const selectInterviewIdToMatchingFragmentId = (state: RootState) =>
  state.interviews.interviewIdToMatchingFragmentId;
